import { observer } from 'mobx-react-lite';
import React, { memo } from 'react';
import { FormattedDate, FormattedMessage, useIntl } from 'react-intl';
import Box from 'ui-box';
import {
  Dialog,
  IDialogProps,
  majorScale,
  Spinner,
  VirtualTable,
  VirtualTableItem,
} from '../../npaper';
import { useStores } from '../../stores';
import { IPaginationFilter } from '../../stores/utils/modelWithCRUD';
import { useFilterFetch } from '../../stores/utils/useFilterFetch';
import {
  IProductEntry,
  IProductEntrySnapshotIn,
  ProductEntry,
} from '../../stores/warehousing/ProductEntryStore';
import { ProductThumbnail } from '../products/ProductThumbnail';

interface IProductEntryDialogProps extends IDialogProps {
  filter?: IPaginationFilter<IProductEntrySnapshotIn>['filter'];
}

const ProductEntryDialog: React.FC<IProductEntryDialogProps> = observer(
  ({ filter: defaultFilter, ...dialogProps }) => {
    const intl = useIntl();

    const { productEntryStore } = useStores();

    const { items, isLoading, fetchMore, total, setFilter, filter } =
      useFilterFetch<typeof ProductEntry>(
        productEntryStore as any,
        {
          filter: defaultFilter,
          ordering: '-created_at',
        },
        false
      );

    return (
      <Dialog
        {...dialogProps}
        title={
          dialogProps.title ||
          intl.formatMessage({ defaultMessage: 'Product entries' })
        }
        maxWidth="1440px !important"
        full
      >
        {!items ? (
          <Box display="flex" padding={majorScale(4)} justifyContent="center">
            <Spinner />
          </Box>
        ) : (
          <Box height="100%" padding={majorScale(2)}>
            <VirtualTable
              isLoading={isLoading}
              items={items as IProductEntry[]}
              total={total}
              ordering={filter.ordering}
              onOrderingChange={(ordering) => {
                setFilter({ filter: filter.filter, ordering });
              }}
              noItemsPlaceholder={
                <Box className="p" padding={majorScale(4)} textAlign="center">
                  <FormattedMessage defaultMessage="No entries found" />
                </Box>
              }
              onRequestMore={async () => fetchMore()}
              renderRow={({ item }) => <MemoizedRow entry={item} />}
              header={[
                {
                  title: intl.formatMessage({
                    defaultMessage: 'Product',
                  }),
                  orderByKeys: ['product__name', 'created_at'],
                },
                {
                  title: intl.formatMessage({
                    defaultMessage: 'Date created',
                  }),
                  orderByKeys: ['created_at'],
                },
                {
                  title: intl.formatMessage({
                    defaultMessage: 'Location',
                  }),
                  orderByKeys: ['location__code', 'created_at'],
                },
                {
                  title: intl.formatMessage({
                    defaultMessage: 'Quantity',
                  }),
                  align: 'right',
                  orderByKeys: ['quantity', 'created_at'],
                },
                {
                  title: intl.formatMessage({
                    defaultMessage: 'Committed',
                  }),
                  align: 'right',
                  orderByKeys: ['committed', 'created_at'],
                },
              ]}
            />
          </Box>
        )}
      </Dialog>
    );
  }
);

const MemoizedRow = memo(
  observer(({ entry }: { entry: IProductEntry }) => {
    return (
      <>
        <VirtualTableItem>
          {entry.product ? (
            <ProductThumbnail product={entry.product} />
          ) : (
            <Spinner size={16} />
          )}
        </VirtualTableItem>
        <VirtualTableItem>
          <FormattedDate value={entry.created_at} format="default" />
        </VirtualTableItem>
        <VirtualTableItem>
          {entry.location ? entry.location.code : <Spinner size={16} />}
        </VirtualTableItem>
        <VirtualTableItem justifyContent="flex-end">
          <span className="font-mono">{entry.quantity}</span>
        </VirtualTableItem>
        <VirtualTableItem justifyContent="flex-end">
          <span className="font-mono">{entry.committed}</span>
        </VirtualTableItem>
      </>
    );
  })
);

export default ProductEntryDialog;

/*<Table padding={majorScale(2)} backgroundColor="white">
            <thead>
              <tr>
                <th>
                  <FormattedMessage defaultMessage="Product" />
                </th>
                <th>
                  <FormattedMessage defaultMessage="Location" />
                </th>
                <th className="right">
                  <FormattedMessage defaultMessage="Quantity" />
                </th>
                <th className="right">
                  <FormattedMessage defaultMessage="Committed" />
                </th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {items.map((entry) => (
                <tr key={entry.id}>
                  <td>
                    {entry.product ? (
                      <ProductThumbnail product={entry.product} />
                    ) : (
                      <Spinner size={16} />
                    )}
                  </td>
                  <td>
                    {entry.location ? (
                      entry.location.code
                    ) : (
                      <Spinner size={16} />
                    )}
                  </td>
                  <td className="font-mono right">{entry.quantity}</td>
                  <td className="font-mono right">{entry.committed}</td>
                </tr>
              ))}
            </tbody>
          </Table>*/
